import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { Button as BaseButton, FlexColumn, FlexColumnCenter } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import withPermissions from '@ubeya/shared-web/hoc/withPermissions';
import privileges from '@ubeya/shared-web/privileges';
import useEmployeeLeaveManagement from '@ubeya/shared/hooks/employees/useEmployeeLeaveManagement';
import ContractEmptyState from '../EmployeeContracts/ContractEmptyState';
import { RootWrapper } from '../styled';
import AssignContractModal from './modals/AssignContractModal';
import UpdateAssignmentModal from './modals/UpdateAssignmentModal';
import TerminateAssignmentModal from './modals/TerminateAssignmentModal';
import ChangeContractModal from './modals/ChangeContractModal';
import YearResetModal from './modals/YearResetModal';
import AdjustBalanceModal from './modals/AdjustBalanceModal';
import CreateLeaveRecordModal from './modals/CreateLeaveRecordModal';
import CancelLeaveRecordModal from './modals/CancelLeaveRecordModal';
import PayoutAllBalancesModal from './modals/PayoutAllBalancesModal';
import HeaderBar from './components/HeaderBar';
import BalanceTable from './components/BalanceTable';
import LeaveHistory from './components/LeaveHistory';
import LeaveLedgerModal from './components/LeaveLedgerModal';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 0 41px 40px;
  min-width: 500px;
  gap: 24px;
`;

const NoContractPanel = styled(FlexColumnCenter)`
  flex: 1;
  gap: 20px;
  min-height: 320px;
`;

const NoContractCta = styled(BaseButton)`
  padding: 10px 24px;
  line-height: 22px;
  font-weight: 500;
`;

const EmployeeLeaveManagementV2 = ({ data: employeeData }) => {
  const { t } = useContext(ConfigContext);
  const { id: employeeId, branches: employeeBranches = [], firstName = '', lastName = '' } = employeeData || {};
  const employeeName = [firstName, lastName].filter(Boolean).join(' ');

  const {
    isLoadingManagement,
    isLoadingHistory,
    isLoadingMoreHistory,
    management,
    history,
    fetchMoreHistory,
    fetchFullLedger,
    hasMoreHistory,
    assignContract,
    updateAssignment,
    terminateAssignment,
    changeContract,
    yearReset,
    payoutAllBalances,
    adjustBalance,
    createLeaveRecord,
    cancelLeaveRecord,
    fetchExpectedPay
  } = useEmployeeLeaveManagement({ employeeId });

  const [activeModal, setActiveModal] = useState(null);
  const [showLedger, setShowLedger] = useState(false);
  const [recordToCancel, setRecordToCancel] = useState(null);
  const [preselectedBalance, setPreselectedBalance] = useState(null);
  const [adjustInitialValues, setAdjustInitialValues] = useState(null);

  const hasActiveAssignment = !!management && !!management.accountContractV2Id;
  const hasBalances = (management?.balances || []).length > 0;
  const hasManualYearCycleBalance = (management?.balances || []).some((balance) => balance?.yearCycleKind === 'manual');

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setPreselectedBalance(null);
    setAdjustInitialValues(null);
  }, []);
  const closeCancelRecord = useCallback(() => setRecordToCancel(null), []);

  const onAdjust = useCallback((balance) => {
    setPreselectedBalance(balance || null);
    setAdjustInitialValues(null);
    setActiveModal('adjust');
  }, []);
  const onAddUsed = useCallback((balance) => {
    setPreselectedBalance(balance || null);
    setActiveModal('addUsed');
  }, []);

  const onReverseAdjust = useCallback(
    (ledgerRow) => {
      if (!ledgerRow || ledgerRow.eventType !== 'admin-adjust') return;
      const originalAmount = Number(ledgerRow.amount ?? 0);
      if (!Number.isFinite(originalAmount) || originalAmount === 0) return;
      const originalReason = ledgerRow.metadata?.reason || ledgerRow.reason || '';
      const reverseReason = originalReason
        ? t('employeeLeave_ledger_reverseReasonWithOriginal', { reason: originalReason })
        : t('employeeLeave_ledger_reverseReason');
      setPreselectedBalance(null);
      setAdjustInitialValues({
        accountContractV2LeaveId: ledgerRow.accountContractV2LeaveId,
        amount: -originalAmount,
        reason: reverseReason
      });
      setActiveModal('adjust');
    },
    [t]
  );

  const showNoContractState = !isLoadingManagement && !hasActiveAssignment;
  return (
    <RootWrapper>
      <Wrapper>
        {showNoContractState ? (
          <NoContractPanel>
            <ContractEmptyState title="employeeLeave_noContract_title" description="employeeLeave_noContract_body" />
            <NoContractCta
              type="button"
              primary
              onClick={(e) => {
                e.preventDefault();
                setActiveModal('assign');
              }}>
              {t('employeeLeave_noContract_cta')}
            </NoContractCta>
          </NoContractPanel>
        ) : (
          <>
            <HeaderBar
              management={management}
              hasActiveAssignment={hasActiveAssignment}
              hasBalances={hasBalances}
              hasManualYearCycleBalance={hasManualYearCycleBalance}
              onAssign={() => setActiveModal('assign')}
              onChange={() => setActiveModal('change')}
              onTerminate={() => setActiveModal('terminate')}
              onYearReset={() => setActiveModal('yearReset')}
              onPayoutAll={() => setActiveModal('payoutAll')}
              onEditAssignment={() => setActiveModal('editAssignment')}
              onShowLedger={() => setShowLedger(true)}
            />

            <BalanceTable
              management={management}
              isLoading={isLoadingManagement}
              onAdjust={onAdjust}
              onAddUsed={onAddUsed}
            />

            <LeaveHistory history={history} isLoading={isLoadingHistory} onCancelRecord={setRecordToCancel} />
          </>
        )}
      </Wrapper>

      {activeModal === 'assign' && <AssignContractModal onClose={closeModal} assignContract={assignContract} />}
      {activeModal === 'change' && (
        <ChangeContractModal
          onClose={closeModal}
          changeContract={changeContract}
          management={management}
          employeeBranches={employeeBranches}
        />
      )}
      {activeModal === 'terminate' && (
        <TerminateAssignmentModal
          onClose={closeModal}
          terminateAssignment={terminateAssignment}
          management={management}
        />
      )}
      {activeModal === 'yearReset' && (
        <YearResetModal onClose={closeModal} yearReset={yearReset} management={management} />
      )}
      {activeModal === 'adjust' && (
        <AdjustBalanceModal
          onClose={closeModal}
          adjustBalance={adjustBalance}
          management={management}
          preselectedBalance={preselectedBalance}
          initialValues={adjustInitialValues}
        />
      )}
      {activeModal === 'addUsed' && (
        <CreateLeaveRecordModal
          onClose={closeModal}
          createLeaveRecord={createLeaveRecord}
          management={management}
          fetchExpectedPay={fetchExpectedPay}
          employeeId={employeeId}
          employeeBranches={employeeBranches}
          preselectedBalance={preselectedBalance}
          employeeName={employeeName}
          firstName={firstName}
        />
      )}
      {activeModal === 'payoutAll' && (
        <PayoutAllBalancesModal
          onClose={closeModal}
          payoutAllBalances={payoutAllBalances}
          management={management}
          employeeBranches={employeeBranches}
        />
      )}
      {activeModal === 'editAssignment' && (
        <UpdateAssignmentModal onClose={closeModal} updateAssignment={updateAssignment} management={management} />
      )}
      {showLedger && (
        <LeaveLedgerModal
          employeeId={employeeId}
          employeeName={employeeName}
          history={history}
          management={management}
          onReverseAdjust={onReverseAdjust}
          onLoadMore={fetchMoreHistory}
          hasMore={hasMoreHistory}
          isLoadingMore={isLoadingMoreHistory}
          fetchFullLedger={fetchFullLedger}
          onClose={() => setShowLedger(false)}
        />
      )}
      {recordToCancel && (
        <CancelLeaveRecordModal
          onClose={closeCancelRecord}
          cancelLeaveRecord={cancelLeaveRecord}
          record={recordToCancel}
        />
      )}
    </RootWrapper>
  );
};

export default withPermissions(React.memo(EmployeeLeaveManagementV2), {
  action: { privileges: [privileges.MANAGE_EMPLOYEE_HOLIDAY_ACCRUAL] }
});
